import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../i18n/I18nContext';
import api from '../../utils/api';

function AdminReviews() {
  const { user } = useAuth();
  const { t } = useI18n();

  const [reviews, setReviews] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [vendorFilter, setVendorFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [visibleFilter, setVisibleFilter] = useState('');

  useEffect(() => {
    if (user) {
      loadData();
    }
    async function loadData() {
      try {
        const [reviewsData, vendorsData] = await Promise.all([
          api.get('/admin/reviews'),
          api.get('/admin/vendors')
        ]);
        setReviews(reviewsData);
        setVendors(vendorsData);
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    }
  }, [user]);

  useEffect(() => {
    if (user && !loading) {
      loadReviews();
    }
    async function loadReviews() {
      try {
        const params = new URLSearchParams();
        if (vendorFilter) params.append('vendor_id', vendorFilter);
        if (ratingFilter) params.append('rating', ratingFilter);
        if (visibleFilter) params.append('visible', visibleFilter);

        const data = await api.get(`/admin/reviews?${params.toString()}`);
        setReviews(data);
      } catch (err) {
        console.error('Failed to load reviews:', err);
      }
    }
  }, [vendorFilter, ratingFilter, visibleFilter, user, loading]);

  async function toggleVisibility(review) {
    try {
      await api.put(`/admin/reviews/${review.id}/visibility`, {
        visible: !review.visible
      });
      setReviews(prev => prev.map(r =>
        r.id === review.id ? { ...r, visible: !r.visible } : r
      ));
    } catch (err) {
      alert('Failed to update visibility: ' + err.message);
    }
  }

  function renderStars(rating) {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  }

  function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  if (loading) {
    return <div className="flex justify-center" style={{ padding: '40px 0' }}><div className="spinner"></div></div>;
  }

  if (!user) return null;

  return (
    <div>
      {/* Page Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 style={{ fontSize: '20px', fontWeight: '600' }}>{t('admin.reviews')}</h1>
      </div>
        {/* Filters */}
        <div className="card mb-4">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
            <div>
              <label className="label text-xs">{t('admin.vendors')}</label>
              <select
                className="input"
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
              >
                <option value="">{t('admin.allVendors')}</option>
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label text-xs">{t('admin.rating')}</label>
              <select
                className="input"
                value={ratingFilter}
                onChange={(e) => setRatingFilter(e.target.value)}
              >
                <option value="">{t('admin.allRatings')}</option>
                <option value="5">{t('admin.stars', { count: 5 })}</option>
                <option value="4">{t('admin.stars', { count: 4 })}</option>
                <option value="3">{t('admin.stars', { count: 3 })}</option>
                <option value="2">{t('admin.stars', { count: 2 })}</option>
                <option value="1">{t('admin.star')}</option>
              </select>
            </div>
            <div>
              <label className="label text-xs">{t('admin.visibility')}</label>
              <select
                className="input"
                value={visibleFilter}
                onChange={(e) => setVisibleFilter(e.target.value)}
              >
                <option value="">{t('common.all')}</option>
                <option value="true">{t('admin.visible')}</option>
                <option value="false">{t('admin.hidden')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="text-secondary text-sm mb-3">
          {t('admin.reviewsFound', { count: reviews.length })}
        </div>

        {/* Reviews List */}
        {reviews.length === 0 ? (
          <div className="card text-center text-secondary">
            {t('admin.noReviewsFound')}
          </div>
        ) : (
          reviews.map(review => (
            <div key={review.id} className="card mb-3" style={{ opacity: review.visible ? 1 : 0.6 }}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div style={{ color: '#f59e0b', fontSize: '16px', letterSpacing: '2px' }}>
                    {renderStars(review.rating)}
                  </div>
                  <div className="text-xs text-secondary mt-1">
                    {formatDate(review.created_at)}
                  </div>
                </div>
                <button
                  onClick={() => toggleVisibility(review)}
                  className={`btn ${review.visible ? 'btn-secondary' : 'btn-primary'}`}
                  style={{ padding: '4px 12px', fontSize: '12px' }}
                >
                  {review.visible ? t('admin.hideBtn') : t('admin.showBtn')}
                </button>
              </div>

              {review.comment && (
                <p style={{ fontSize: '14px', marginBottom: '12px', lineHeight: '1.5' }}>
                  "{review.comment}"
                </p>
              )}

              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                <div className="flex gap-4 flex-wrap">
                  <span><strong>{t('admin.offer')}:</strong> {review.offer_title}</span>
                  <span><strong>{t('admin.vendors')}:</strong> {review.vendor_name}</span>
                  <span><strong>{t('admin.properties')}:</strong> {review.property_name}</span>
                </div>
              </div>

              {!review.visible && (
                <div style={{ marginTop: '8px', padding: '4px 8px', background: 'var(--error)', color: 'white', borderRadius: '4px', fontSize: '11px', display: 'inline-block' }}>
                  {t('admin.hiddenFromReports')}
                </div>
              )}
            </div>
          ))
        )}
    </div>
  );
}

export default AdminReviews;
